// Cookie-consent state + Google Consent Mode v2 bridge.
//
// The gtag snippet in index.html sets all storage to `denied` by default, so GA
// loads but sends only cookieless, consent-aware pings. The banner
// (components/CookieConsent.tsx) records the visitor's choice here, which flips
// `analytics_storage` via gtag's consent `update` — no cookies until they accept.

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type ConsentChoice = 'granted' | 'denied';

const STORAGE_KEY = 'byaft:cookie-consent:v1';

/** Fired after the choice changes so the banner can close / settings can reflect it. */
export const CONSENT_CHANGE_EVENT = 'byaft:consent-change';
/** Fired by the footer "Cookie settings" control to re-open the banner. */
export const OPEN_CONSENT_EVENT = 'byaft:open-cookie-consent';

/** The stored choice, or null if the visitor hasn't decided yet. */
export function getConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

/** Push the choice to gtag's Consent Mode (analytics only; ads stay denied). */
export function updateConsent(choice: ConsentChoice): void {
  window.gtag?.('consent', 'update', { analytics_storage: choice });
}

// Google Analytics cookies: _ga, _ga_<stream>, _gid, _gat*.
const GA_COOKIE_RE = /^(_ga|_gid|_gat)/;

/** Expire one cookie across every domain/path scope GA might have used. */
function deleteCookie(name: string): void {
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const { hostname, pathname } = window.location;
  const labels = hostname.split('.');
  // host, .host, and each parent registrable domain (e.g. byaft.com / .byaft.com)
  const domains = new Set<string>([hostname, `.${hostname}`]);
  for (let i = 0; i < labels.length - 1; i += 1) {
    const d = labels.slice(i).join('.');
    domains.add(d);
    domains.add(`.${d}`);
  }
  const paths = new Set<string>(['/', pathname]);
  for (const path of paths) {
    document.cookie = `${name}=; expires=${past}; path=${path}`;
    for (const domain of domains) {
      document.cookie = `${name}=; expires=${past}; path=${path}; domain=${domain}`;
    }
  }
}

/**
 * Remove GA cookies after consent is withdrawn. `gtag` consent `update` stops
 * GA from setting *new* cookies, but any `_ga`/`_gid` cookies created while
 * consent was granted must be deleted to actually honour the withdrawal.
 */
export function clearAnalyticsCookies(): void {
  try {
    const names = document.cookie
      .split(';')
      .map((c) => c.split('=')[0].trim())
      .filter(Boolean);
    for (const name of names) {
      if (GA_COOKIE_RE.test(name)) deleteCookie(name);
    }
  } catch {
    /* no-op */
  }
}

/** Record a choice, update Consent Mode, and notify listeners. */
export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* storage unavailable — still update gtag for this session */
  }
  updateConsent(choice);
  // Withdrawing consent must clear the analytics cookies already set.
  if (choice === 'denied') clearAnalyticsCookies();
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: choice }));
  } catch {
    /* no-op */
  }
}

/** On startup, re-apply a previously granted choice (default is denied). */
export function applyStoredConsent(): void {
  const stored = getConsent();
  if (stored === 'granted') updateConsent('granted');
  // Also sweep any lingering GA cookies for visitors who have declined
  // (e.g. before this fix shipped, or who cleared consent on another page).
  else if (stored === 'denied') clearAnalyticsCookies();
}
